import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Spinner, Alert, Table, OverlayTrigger, Tooltip, Dropdown, Form, Button, Badge } from 'react-bootstrap';
import { courseService, commonService } from '../../services/api';

const getHiddenCoursesStorageKey = (year) => `sportus_enrollment_stats_hidden_courses_${year}`;

const METRIC_DESCRIPTIONS = {
  proveNuovi: {
    label: 'Prove nuovi',
    description: 'Atleti che hanno effettuato una o più lezioni di prova in questo corso durante la stagione sportiva.',
  },
  nuoviIscritti: {
    label: 'Nuovi iscritti',
    description: 'Atleti al primo anno assoluto nell\'associazione (senza storico di corsi nelle stagioni precedenti) che hanno versato la quota associativa e pagato almeno una quota del corso.',
  },
  giaIscritti: {
    label: 'Già iscritti',
    description: 'Atleti presenti nello storico delle stagioni passate che rinnovano l\'iscrizione nella stagione corrente (incluso cambio corso o disciplina), con quota associativa e corso pagati.',
  },
  iscrittiTotali: {
    label: 'Iscritti Totali',
    description: 'Totale corsisti con quota associativa versata e pagamento valido per la stagione in corso (Nuovi Iscritti + Già Iscritti). Le sole prove non perfezionate sono escluse.',
  },
  conclusoAnno: {
    label: 'Concluso anno',
    description: 'Atleti regolarmente iscritti la cui frequenza e copertura economica si estendono fino al termine della stagione sportiva (giugno).',
  },
  persi: {
    label: 'Persi',
    description: 'Atleti regolarmente iscritti che hanno interrotto la frequenza prima del termine della stagione (Iscritti Totali meno Concluso anno). Chi ha fatto solo la prova è escluso.',
  },
  tassoRetention: {
    label: '% Retention',
    description: 'Tasso di fidelizzazione: percentuale di corsisti che hanno concluso la stagione precedente e hanno rinnovato l\'iscrizione nella stagione corrente.',
  },
  percentualeIscrittiTotali: {
    label: '% su Iscritti Totali',
    description: 'Incidenza percentuale rispetto al totale degli iscritti della stagione corrente.',
  },
};

const EnrollmentStatisticsTable = ({ academicYear }) => {
  const [data, setData] = useState([]);
  const [overallData, setOverallData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentYearFallback, setCurrentYearFallback] = useState(null);

  // If no academicYear prop is passed (e.g. filter is "Tutti gli anni"), resolve current academic year
  useEffect(() => {
    if (!academicYear) {
      commonService
        .getCurrentAcademicYear()
        .then((res) => {
          if (res && res.success && res.data) {
            setCurrentYearFallback(res.data);
          } else {
            setCurrentYearFallback(new Date().getFullYear());
          }
        })
        .catch(() => {
          setCurrentYearFallback(new Date().getFullYear());
        });
    }
  }, [academicYear]);

  const activeAcademicYear = academicYear || currentYearFallback;

  const [hiddenCourses, setHiddenCourses] = useState([]);

  // Load hidden courses from localStorage when activeAcademicYear changes
  useEffect(() => {
    if (!activeAcademicYear) return;
    try {
      const stored = localStorage.getItem(getHiddenCoursesStorageKey(activeAcademicYear));
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setHiddenCourses(parsed);
          return;
        }
      }
    } catch (e) {
      console.warn('Errore nel recupero delle colonne nascoste da localStorage:', e);
    }
    setHiddenCourses([]);
  }, [activeAcademicYear]);

  const updateHiddenCourses = useCallback(
    (newHidden) => {
      setHiddenCourses(newHidden);
      if (activeAcademicYear) {
        try {
          localStorage.setItem(
            getHiddenCoursesStorageKey(activeAcademicYear),
            JSON.stringify(newHidden)
          );
        } catch (e) {
          console.warn('Errore nel salvataggio delle colonne nascoste in localStorage:', e);
        }
      }
    },
    [activeAcademicYear]
  );

  const toggleCourseVisibility = useCallback(
    (courseName) => {
      const isHidden = hiddenCourses.includes(courseName);
      const next = isHidden
        ? hiddenCourses.filter((name) => name !== courseName)
        : [...hiddenCourses, courseName];
      updateHiddenCourses(next);
    },
    [hiddenCourses, updateHiddenCourses]
  );

  const showAllCourses = useCallback(() => {
    updateHiddenCourses([]);
  }, [updateHiddenCourses]);

  const hideAllCourses = useCallback(() => {
    const allNames = data.map((c) => c.courseName);
    updateHiddenCourses(allNames);
  }, [data, updateHiddenCourses]);

  const visibleData = useMemo(() => {
    if (!hiddenCourses || hiddenCourses.length === 0) return data;
    return data.filter((col) => !hiddenCourses.includes(col.courseName));
  }, [data, hiddenCourses]);

  useEffect(() => {
    if (!activeAcademicYear) return;

    let isMounted = true;
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await courseService.getCourseEnrollmentStatistics(activeAcademicYear);
        if (!isMounted) return;

        if (response.success && response.data) {
          if (Array.isArray(response.data)) {
            setData(response.data);
            setOverallData(null);
          } else if (response.data.courses) {
            setData(Array.isArray(response.data.courses) ? response.data.courses : []);
            setOverallData(response.data.overall || null);
          } else {
            setData([]);
            setOverallData(null);
          }
        } else {
          setError(response.message || 'Errore nel caricamento delle statistiche iscrizioni');
        }
      } catch (err) {
        if (isMounted) {
          setError('Errore di connessione al server');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [activeAcademicYear]);

  if (!activeAcademicYear) {
    return null;
  }

  if (loading) {
    return (
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body className="text-center py-4">
          <Spinner animation="border" variant="primary" size="sm" className="me-2" />
          <span className="text-muted">
            Calcolo statistiche iscrizioni per l'anno {activeAcademicYear}/{activeAcademicYear + 1} in corso...
          </span>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="mb-4">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error}
      </Alert>
    );
  }

  if (data.length === 0) {
    return (
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body className="text-muted text-center py-3">
          <i className="fas fa-info-circle me-2"></i>
          Nessun dato di iscrizione trovato per l'anno sportivo {activeAcademicYear}/{activeAcademicYear + 1}.
        </Card.Body>
      </Card>
    );
  }

  // Calcolo totali fallback nel caso in cui overall non sia presente
  const calculatedTotals = data.reduce(
    (acc, curr) => {
      acc.proveNuovi += curr.proveNuovi || 0;
      acc.nuoviIscritti += curr.nuoviIscritti || 0;
      acc.giaIscritti += curr.giaIscritti || 0;
      acc.conclusoAnno += curr.conclusoAnno || 0;
      acc.persi += curr.persi || 0;
      acc.iscrittiTotali += curr.iscrittiTotali || 0;
      acc.conclusoAnnoPrecedente += curr.conclusoAnnoPrecedente || 0;
      acc.riconfermati += curr.riconfermati || 0;
      return acc;
    },
    {
      proveNuovi: 0,
      nuoviIscritti: 0,
      giaIscritti: 0,
      conclusoAnno: 0,
      persi: 0,
      iscrittiTotali: 0,
      conclusoAnnoPrecedente: 0,
      riconfermati: 0,
    }
  );

  const totals = overallData || {
    ...calculatedTotals,
    tassoRetention:
      calculatedTotals.conclusoAnnoPrecedente > 0
        ? Math.round((calculatedTotals.riconfermati / calculatedTotals.conclusoAnnoPrecedente) * 1000) / 10
        : null,
  };

  const renderPercentage = (value, total) => {
    if (!total || total === 0) return '0%';
    const pct = (value / total) * 100;
    return `${Number.isInteger(pct) ? pct : pct.toFixed(1)}%`;
  };

  const renderRetentionRate = (rate) => {
    if (rate === null || rate === undefined || isNaN(rate)) return '-';
    const num = Number(rate);
    return `${Number.isInteger(num) ? num : num.toFixed(1)}%`;
  };

  const renderMetricLabel = (key, customClass = '') => {
    const metric = METRIC_DESCRIPTIONS[key];
    if (!metric) return key;

    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id={`tooltip-${key}`}>
            {metric.description}
          </Tooltip>
        }
      >
        <span
          className={`d-inline-flex align-items-center gap-1 ${customClass}`}
          style={{ cursor: 'help' }}
          data-testid={`metric-tooltip-trigger-${key}`}
        >
          <span>{metric.label}</span>
          <i className="fas fa-info-circle text-muted" style={{ fontSize: '0.85em', opacity: 0.8 }} />
        </span>
      </OverlayTrigger>
    );
  };

  return (
    <Card className="shadow-sm border-0 mb-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <h5 className="card-title text-primary mb-0 fw-bold">
              <i className="fas fa-users-cog me-2"></i>
              Riepilogo Iscrizioni ({activeAcademicYear}/{activeAcademicYear + 1})
            </h5>
            {!academicYear && (
              <span className="badge bg-secondary">
                Anno corrente predefinito
              </span>
            )}
          </div>

          <div className="d-flex align-items-center gap-2">
            <Dropdown autoClose="outside" align="end">
              <Dropdown.Toggle
                variant={hiddenCourses.length > 0 ? 'outline-warning' : 'outline-secondary'}
                size="sm"
                className="d-flex align-items-center gap-2"
                id="dropdown-course-visibility"
                data-testid="toggle-course-visibility-dropdown"
              >
                <i className="fas fa-columns" />
                <span>Corsi visibili</span>
                <Badge
                  bg={hiddenCourses.length > 0 ? 'warning' : 'secondary'}
                  text={hiddenCourses.length > 0 ? 'dark' : 'white'}
                >
                  {visibleData.length}/{data.length}
                </Badge>
              </Dropdown.Toggle>

              <Dropdown.Menu className="p-2 shadow-sm" style={{ minWidth: '260px' }}>
                <div className="d-flex justify-content-between align-items-center pb-2 mb-2 border-bottom">
                  <span className="small fw-bold text-muted">Personalizza colonne</span>
                  <div className="btn-group btn-group-sm">
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-decoration-none me-2 text-primary small"
                      onClick={showAllCourses}
                      disabled={hiddenCourses.length === 0}
                      data-testid="show-all-courses-btn"
                    >
                      Mostra tutti
                    </Button>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-decoration-none text-secondary small"
                      onClick={hideAllCourses}
                      disabled={visibleData.length === 0}
                      data-testid="hide-all-courses-btn"
                    >
                      Nascondi tutti
                    </Button>
                  </div>
                </div>

                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  {data.map((col) => {
                    const isVisible = !hiddenCourses.includes(col.courseName);
                    return (
                      <div
                        key={col.courseName}
                        className="py-1 px-2 rounded"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Form.Check
                          type="checkbox"
                          id={`col-toggle-${col.courseName.replace(/[^a-zA-Z0-9]/g, '-')}`}
                          label={col.courseName}
                          checked={isVisible}
                          onChange={() => toggleCourseVisibility(col.courseName)}
                          className="user-select-none mb-0 small"
                          data-testid={`course-checkbox-${col.courseName}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {visibleData.length === 0 && (
          <div className="alert alert-info py-2 px-3 small d-flex align-items-center justify-content-between mb-3">
            <span>
              <i className="fas fa-eye-slash me-2" />
              Tutte le colonne dei corsi sono attualmente nascoste.
            </span>
            <Button
              variant="link"
              size="sm"
              className="p-0 text-decoration-none"
              onClick={showAllCourses}
            >
              Mostra tutti i corsi
            </Button>
          </div>
        )}

        <div className="table-responsive">
          <Table bordered hover size="sm" className="mb-0 text-center align-middle">
            <thead className="table-light">
              <tr>
                <th className="text-start">Metrica</th>
                {visibleData.map((col) => (
                  <th key={col.courseName}>{col.courseName}</th>
                ))}
                <th className="bg-light fw-bold">Totale</th>
                <th className="bg-light fw-bold">
                  {renderMetricLabel('percentualeIscrittiTotali')}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="text-start">
                  {renderMetricLabel('proveNuovi', 'fw-semibold text-muted')}
                </td>
                {visibleData.map((col) => (
                  <td key={col.courseName}>{col.proveNuovi}</td>
                ))}
                <td className="bg-light fw-bold">{totals.proveNuovi}</td>
                <td className="bg-light text-muted">-</td>
              </tr>
              <tr>
                <td className="text-start">
                  {renderMetricLabel('nuoviIscritti', 'fw-semibold text-primary')}
                </td>
                {visibleData.map((col) => (
                  <td key={col.courseName}>{col.nuoviIscritti}</td>
                ))}
                <td className="bg-light fw-bold text-primary">{totals.nuoviIscritti}</td>
                <td className="bg-light text-muted">{renderPercentage(totals.nuoviIscritti, totals.iscrittiTotali)}</td>
              </tr>
              <tr>
                <td className="text-start">
                  {renderMetricLabel('giaIscritti', 'fw-semibold text-success')}
                </td>
                {visibleData.map((col) => (
                  <td key={col.courseName}>{col.giaIscritti}</td>
                ))}
                <td className="bg-light fw-bold text-success">{totals.giaIscritti}</td>
                <td className="bg-light text-muted">{renderPercentage(totals.giaIscritti, totals.iscrittiTotali)}</td>
              </tr>
              <tr className="table-light">
                <td className="text-start">
                  {renderMetricLabel('iscrittiTotali', 'fw-bold text-dark')}
                </td>
                {visibleData.map((col) => (
                  <td key={col.courseName} className="fw-bold">{col.iscrittiTotali}</td>
                ))}
                <td className="fw-bold">{totals.iscrittiTotali}</td>
                <td className="text-muted">100%</td>
              </tr>
              <tr>
                <td className="text-start">
                  {renderMetricLabel('conclusoAnno', 'fw-semibold text-info')}
                </td>
                {visibleData.map((col) => (
                  <td key={col.courseName}>{col.conclusoAnno}</td>
                ))}
                <td className="bg-light fw-bold text-info">{totals.conclusoAnno}</td>
                <td className="bg-light text-muted">{renderPercentage(totals.conclusoAnno, totals.iscrittiTotali)}</td>
              </tr>
              <tr>
                <td className="text-start">
                  {renderMetricLabel('persi', 'fw-semibold text-danger')}
                </td>
                {visibleData.map((col) => (
                  <td key={col.courseName}>{col.persi}</td>
                ))}
                <td className="bg-light fw-bold text-danger">{totals.persi}</td>
                <td className="bg-light text-muted">{renderPercentage(totals.persi, totals.iscrittiTotali)}</td>
              </tr>
              <tr>
                <td className="text-start">
                  {renderMetricLabel('tassoRetention', 'fw-semibold text-secondary')}
                </td>
                {visibleData.map((col) => (
                  <td key={col.courseName} className="fw-semibold">
                    {renderRetentionRate(col.tassoRetention)}
                  </td>
                ))}
                <td className="bg-light fw-bold text-secondary">
                  {renderRetentionRate(totals.tassoRetention)}
                </td>
                <td className="bg-light text-muted">-</td>
              </tr>
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
};

export default EnrollmentStatisticsTable;
